import { ahtWtInitialSupplyChain } from "./aht-wt-initial-supply-chain";
import type { PathwayChain } from "../types/chain.types";

const chainRegistry: Record<string, PathwayChain> = {
  [ahtWtInitialSupplyChain.id]: ahtWtInitialSupplyChain,
};

export function getChain(chainId: string): PathwayChain | undefined {
  return chainRegistry[chainId];
}

export function resolveNextPathway(
  chainId: string,
  context: Record<string, unknown>,
  audit: any,
): string | null {
  // Re-initiation picks the drug FIRST, then runs the shared assessment.
  // Initial/transfer go straight to the shared assessment.
  if (chainId === "aht-wt-selector-chain") {
    const consultationType = context["aht-sel-consultation-type"];
    if (consultationType === "reinitiation")
      return "aht-wt-reinitiation-drug-select";
    return "aht-wt-shared-assessment";
  }

  // Drug already chosen — outcome title is "Loading shared assessment...".
  if (chainId === "aht-wt-reinitiation-drug-select-chain") {
    return "aht-wt-shared-assessment";
  }

  // All consultation types run the full drug-selection screening. For
  // re-initiation the drug picked earlier is surfaced at the drug_resolution
  // step (see reinitiationDrugId) rather than short-circuiting the screening.
  if (chainId === "aht-wt-shared-assessment-chain") {
    return "aht-weight-selection";
  }

  // Drug resolved at the end of weight-selection. The target pathway depends on
  // the consultation type, not just the drug: transfers get the drug's
  // -transfer pathway, initial and re-initiation both get -initial.
  // Mirrors the pathwayMap in the builder app's SupplyPathwayTemplate.
  const PATHWAY_MAP: Record<string, Record<string, string>> = {
    initial: {
      tirzepatide: "aht-wt-tirzepatide-initial",
      semaglutide: "aht-wt-semaglutide-initial",
      oral_semaglutide: "aht-wt-oral-semaglutide-initial",
      foundayo: "aht-wt-foundayo-initial",
      liraglutide: "aht-wt-liraglutide-initial",
      mysimba: "aht-wt-mysimba-initial",
      orlistat: "aht-wt-orlistat-initial",
    },
    transfer: {
      tirzepatide: "aht-wt-tirzepatide-transfer",
      semaglutide: "aht-wt-semaglutide-transfer",
      foundayo: "aht-wt-foundayo-transfer",
      liraglutide: "aht-wt-liraglutide-transfer",
      mysimba: "aht-wt-mysimba-transfer",
      orlistat: "aht-wt-orlistat-transfer",
    },
    reinitiation: {
      tirzepatide: "aht-wt-tirzepatide-initial",
      semaglutide: "aht-wt-semaglutide-initial",
      oral_semaglutide: "aht-wt-oral-semaglutide-initial",
      // Foundayo has its own re-initiation pathway: the restart dose depends on the gap length.
      foundayo: "aht-wt-foundayo-reinitiation",
      liraglutide: "aht-wt-liraglutide-initial",
    },
  };

  const chain = chainRegistry[chainId];
  if (chain?.drugInitialMap) {
    const drugId =
      audit?.drugResolution?.resolvedDrugId ?? context["resolvedDrugId"];
    if (!drugId) return null;
    const type = (context["aht-sel-consultation-type"] as string) || "initial";
    return (
      PATHWAY_MAP[type]?.[drugId as string] ??
      chain.drugInitialMap[drugId as string] ??
      null
    );
  }

  return null;
}
